import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { useSession } from "@/context/SessionProvider";
import { toast } from "react-toastify";

import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/airbnb.css";

import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/plugins/monthSelect/style.css";

// Helpers sencillos
const today = new Date();
const pad = (n) => String(n).padStart(2, "0");
const yyyy = today.getFullYear();
const mm = pad(today.getMonth() + 1);
const startOfMonth = `${yyyy}-${mm}-01`;
const endOfMonth = `${yyyy}-${mm}-${pad(
    new Date(yyyy, today.getMonth() + 1, 0).getDate()
)}`;
const todayStr = `${yyyy}-${mm}-${pad(today.getDate())}`;
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);
const yesterdayStr = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`;

//  FIX TZ: parse YYYY-MM-DD como fecha LOCAL (evita brincos por UTC)
const ymdToLocalDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = String(ymd).split("-").map((x) => Number(x));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12, 0, 0, 0); // mediodía para evitar DST edge-cases
};

//  Para month picker: aunque guardes fin de mes (YYYY-MM-DD), se muestra como el mes (día 1)
const ymdToMonthLocalDate = (ymd) => {
    if (!ymd) return null;
    const [y, m] = String(ymd).split("-").map((x) => Number(x));
    if (!y || !m) return null;
    return new Date(y, m - 1, 1, 12, 0, 0, 0);
};

const toYmd = (d) => {
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    return `${y}-${m}-${day}`;
};

const monthStartYmd = (d) => {
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    return `${y}-${m}-01`;
};

const monthEndYmd = (d) => {
    const y = d.getFullYear();
    const mNum = d.getMonth() + 1; // 1..12
    const m = pad(mNum);
    const lastDay = new Date(y, mNum, 0).getDate(); // último día del mes
    return `${y}-${m}-${pad(lastDay)}`;
};

// ─── SearchableSelect ────────────────────────────────────────────────────────
function SearchableSelect({ label, value, onChange, options, placeholder = "Todos", disabled = false, valueKey, labelKey }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const containerRef = useRef(null);

    const filtered = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => String(o[labelKey] || "").toLowerCase().includes(q));
    }, [options, query, labelKey]);

    const selectedLabel = useMemo(() => {
        if (!value) return null;
        const found = options.find((o) => String(o[valueKey]) === String(value));
        return found ? found[labelKey] : null;
    }, [value, options, valueKey, labelKey]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
        setQuery("");
    };

    return (
        <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-600">{label}</label>
            <div ref={containerRef} className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => { if (!disabled) setOpen((p) => !p); }}
                    className={`w-full rounded-md border px-2 py-1.5 text-sm text-left flex items-center justify-between gap-1 bg-white ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-slate-400"}`}
                >
                    <span className={selectedLabel ? "text-slate-800 truncate" : "text-slate-400"}>
                        {selectedLabel || `- ${placeholder} -`}
                    </span>
                    <svg className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                        <div className="p-1.5 border-b">
                            <input
                                autoFocus
                                type="text"
                                className="w-full rounded px-2 py-1 text-xs border border-slate-200 focus:outline-none focus:border-blue-400"
                                placeholder="Buscar..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                        <ul className="max-h-48 overflow-y-auto py-1">
                            <li
                                className="px-3 py-1.5 text-xs text-slate-500 cursor-pointer hover:bg-slate-50"
                                onClick={() => handleSelect("")}
                            >
                                — {placeholder} —
                            </li>
                            {filtered.map((o) => (
                                <li
                                    key={o[valueKey]}
                                    onClick={() => handleSelect(String(o[valueKey]))}
                                    className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-blue-50 hover:text-blue-700 ${String(o[valueKey]) === String(value) ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700"}`}
                                >
                                    {o[labelKey]}
                                </li>
                            ))}
                            {filtered.length === 0 && (
                                <li className="px-3 py-2 text-xs text-slate-400 text-center">Sin resultados</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── MultiSelect (empresa / cliente) ─────────────────────────────────────────
// Selecciona varios. values = arreglo de strings; [] significa "todas/todos".
function MultiSelect({ label, values, onChange, options, placeholder = "Todos", disabled = false, valueKey, labelKey }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const containerRef = useRef(null);

    const selectedSet = useMemo(() => new Set((values || []).map(String)), [values]);

    const filtered = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => String(o[labelKey] || "").toLowerCase().includes(q));
    }, [options, query, labelKey]);

    const summary = useMemo(() => {
        if (!values || values.length === 0) return null;
        if (values.length === 1) {
            const found = options.find((o) => String(o[valueKey]) === String(values[0]));
            return found ? found[labelKey] : "1 seleccionado";
        }
        return `${values.length} seleccionados`;
    }, [values, options, valueKey, labelKey]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") { setOpen(false); setQuery(""); }
        };
        document.addEventListener("mousedown", handler);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const toggle = (val) => {
        const v = String(val);
        const next = new Set(selectedSet);
        if (next.has(v)) next.delete(v); else next.add(v);
        onChange([...next]);
    };

    const clearAll = () => onChange([]);

    const Check = () => (
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
    );

    const allSelected = !values || values.length === 0;

    return (
        <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-600">{label}</label>
            <div ref={containerRef} className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => { if (!disabled) setOpen((p) => !p); }}
                    className={`w-full rounded-md border px-2 py-1.5 text-sm text-left flex items-center justify-between gap-1 bg-white ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-slate-400"}`}
                >
                    <span className={summary ? "text-slate-800 truncate" : "text-slate-400"}>
                        {summary || `- ${placeholder} -`}
                    </span>
                    <svg className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                        <div className="p-1.5 border-b flex items-center gap-1.5">
                            <input
                                autoFocus
                                type="text"
                                className="flex-1 min-w-0 rounded px-2 py-1 text-xs border border-slate-200 focus:outline-none focus:border-blue-400"
                                placeholder="Buscar..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            {!allSelected && (
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    className="text-[11px] text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded hover:bg-slate-50 shrink-0"
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>
                        <ul className="max-h-48 overflow-y-auto py-1">
                            <li
                                className="px-3 py-1.5 text-xs text-slate-500 cursor-pointer hover:bg-slate-50 flex items-center gap-2"
                                onClick={clearAll}
                            >
                                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${allSelected ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                                    {allSelected && <Check />}
                                </span>
                                — {placeholder} (todas) —
                            </li>
                            {filtered.map((o) => {
                                const checked = selectedSet.has(String(o[valueKey]));
                                return (
                                    <li
                                        key={o[valueKey]}
                                        onClick={() => toggle(o[valueKey])}
                                        className="px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 flex items-center gap-2"
                                    >
                                        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                                            {checked && <Check />}
                                        </span>
                                        <span className="truncate">{o[labelKey]}</span>
                                    </li>
                                );
                            })}
                            {filtered.length === 0 && (
                                <li className="px-3 py-2 text-xs text-slate-400">Sin resultados</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────────────────

// Mapea columnas a tipo de dato (para sort)
const COLUMN_TYPE = {
    cliente: "text",
    pais: "text",
    troncal: "text",
    fecha: "date",
    llamadas_fijo: "number",
    minutos_fijo: "number",
    llamadas_movil: "number",
    minutos_movil: "number",
    buzon: "number",
    llamadas_pais: "number",
    minutos_pais: "number",
    llamadas_entrada: "number",
    minutos_entrada: "number",
    costo_fijo: "number",
    costo_movil: "number",
    costo_pais: "number",
    costo_buzon: "number",
    costo_total: "number",
};

// Función para formatear fecha según el modo
function formatFecha(fecha, modo) {
    if (!fecha) return "";
    if (modo === "mensual") {
        return fecha.substring(0, 7);
    }
    return fecha;
}

function ReportesPanel({ setResumen, setDetalleData, mode }) {
    const { user } = useSession();
    const isFirstRender = useRef(true);

    const [empresasSeleccionadas, setEmpresasSeleccionadas] = useState([]);
    const [clientesSeleccionados, setClientesSeleccionados] = useState([]);

    const [modo, setModo] = useState("diario");
    const [carrier, setCarrier] = useState("rcc1"); // rcc1 (Clearcom) | rn1 (Netbox)
    const [fechaInicio, setFechaInicio] = useState(yesterdayStr);
    const [fechaFin, setFechaFin] = useState(todayStr);
    const [resumen, setResumenLocal] = useState([]);
    const [resumenMes, setResumenMes] = useState([]); // mes completo, solo para el promedio/dia
    const [loading, setLoading] = useState(false);
    const [empresas, setEmpresas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [columnaOrdenada, setColumnaOrdenada] = useState("fecha");
    const [orden, setOrden] = useState("asc");
    const [quickSearch, setQuickSearch] = useState("");
    const [onepbxData, setOnepbxData] = useState([]);

    // Llaves estables para dependencias de effects (los arreglos cambian de identidad en cada render)
    const empresasKey = empresasSeleccionadas.join(",");
    const clientesKey = clientesSeleccionados.join(",");

    // Cambiar de carrier limpia las selecciones (los catálogos de empresa/cliente difieren)
    const cambiarCarrier = (next) => {
        if (next === carrier) return;
        setCarrier(next);
        setEmpresasSeleccionadas([]);
        setClientesSeleccionados([]);
    };

    //  Mensual: limitar al mes actual (meses futuros no)
    const maxMonthDate = new Date(yyyy, today.getMonth(), 1, 12, 0, 0, 0);
    //  min para Mes Fin (por Mes Inicio) en modo mensual
    const minMonthDateFromInicio = ymdToMonthLocalDate(fechaInicio);

    const isAdmin = !!user && (user.perfil_id === 1 || user.perfil_id === 5);

    //  Flatpickr: opciones mensuales (solo meses)
    const monthPickerOptions = {
        plugins: [
            monthSelectPlugin({
                shorthand: true,
                dateFormat: "Y-m", // valor interno (dateStr)
                altFormat: "F Y", // lo que ve el usuario
            }),
        ],
        altInput: true,
        allowInput: false,
        disableMobile: true,
        maxDate: maxMonthDate,
    };

    //  Flatpickr: opciones diarias (solo días)
    const dayPickerOptions = {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: false,
        disableMobile: true,
        maxDate: today, // (no hoyStr) mejor Date para evitar parsing raro
    };

    const parseLocalDateTs = (val) => {
        if (!val) return 0;
        const d = ymdToLocalDate(val);
        return d ? d.getTime() : 0;
    };

    const parseDateTimeTs = (val) => {
        if (!val) return 0;
        return new Date(val).getTime();
    };

    const getSortableValue = (row, col) => {
        const v = row[col];
        if (col === "fecha") return parseDateTimeTs(v);
        const type = COLUMN_TYPE[col] || "text";
        if (type === "number") {
            return Number(v || 0);
        }
        if (type === "date") {
            return parseLocalDateTs(v);
        }
        return String(v || "").toLowerCase();
    };

    const ordenarPor = (col) => {
        if (columnaOrdenada === col) {
            setOrden((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setColumnaOrdenada(col);
            setOrden("asc");
        }
    };

    // 1) Cargar empresas
    useEffect(() => {
        const fetchEmpresas = async () => {
            try {
                const { data } = await axios.get(API_ROUTES.empresas, {
                    params: { fuente: carrier },
                });
                setEmpresas(data || []);

                if (!isAdmin && user?.empresa_id) {
                    setEmpresasSeleccionadas([String(user.empresa_id)]);
                }
            } catch (error) {
                console.error("Error al cargar empresas:", error);
                toast.error("No se pudieron cargar las empresas.");
            }
        };
        fetchEmpresas();
    }, [isAdmin, user, carrier]);

    // 2) Cargar clientes (union de todas las empresas seleccionadas)
    useEffect(() => {
        const fetchClientes = async () => {
            if (!empresasSeleccionadas.length) {
                setClientes([]);
                return;
            }

            try {
                const results = await Promise.all(
                    empresasSeleccionadas.map((id) =>
                        axios.get(API_ROUTES.clientes(id), { params: { fuente: carrier } })
                    )
                );

                const merged = [];
                const seen = new Set();
                for (const res of results) {
                    for (const c of res.data || []) {
                        const key = String(c.cliente_id);
                        if (!seen.has(key)) {
                            seen.add(key);
                            merged.push(c);
                        }
                    }
                }
                setClientes(merged);

                // Podar clientes seleccionados que ya no pertenecen a las empresas activas
                setClientesSeleccionados((prev) =>
                    prev.filter((cid) => seen.has(String(cid)))
                );
            } catch (error) {
                console.error("Error al cargar clientes:", error);
                toast.error("No se pudieron cargar los clientes.");
            }
        };

        fetchClientes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresasKey, carrier]);

    // 3) OnePBX activos — para mostrar badge de costo excedente en la tabla
    useEffect(() => {
        const fetchOnePBX = async () => {
            // OnePBX solo aplica a RCC1 y a una sola empresa; en RN1 o con varias se omite
            if (carrier !== "rcc1" || empresasSeleccionadas.length !== 1) {
                setOnepbxData([]);
                return;
            }
            try {
                const { data } = await axios.get(
                    API_ROUTES.cargosRecurrentes.onepbxActivos(empresasSeleccionadas[0]),
                    { withCredentials: true }
                );
                setOnepbxData(Array.isArray(data) ? data : []);
            } catch {
                setOnepbxData([]);
            }
        };
        fetchOnePBX();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresasKey, carrier]);

    /**
     * Dado el total de minutos y la tabla de tarifas Flat del cliente,
     * encuentra el rango que aplica y calcula tarifa × minutos.
     * - tarifa === 0  → dentro del plan, sin costo adicional
     * - tarifa  >  0  → hay excedente a cobrar
     */
    /**
     * Cálculo específico para OnePBX:
     * Ignora los rangos con tarifa $0.00 (esos están cubiertos por la mensualidad)
     * y siempre multiplica los minutos por la primera tarifa con precio > 0.
     */
    const calcularCostoOnePBX = (minutosTotal, tarifasFlat) => {
        if (!tarifasFlat || !tarifasFlat.length) return null;
        const mins = Math.ceil(Number(minutosTotal) || 0);

        // Deduplicar por rango, quedarse con la más reciente
        const rangosUnicos = {};
        for (const t of tarifasFlat) {
            const key = `${t.rango_min_inicio}-${t.rango_min_fin}`;
            if (!rangosUnicos[key]) rangosUnicos[key] = t;
        }
        const rangos = Object.values(rangosUnicos).sort(
            (a, b) => Number(a.rango_min_inicio) - Number(b.rango_min_inicio)
        );

        // Tomar la primera tarifa con precio > 0 (ignorar los rangos gratuitos del plan)
        const tarifaPagada = rangos.find((r) => Number(r.tarifa) > 0);
        if (!tarifaPagada) return null;

        const tarifa = Number(tarifaPagada.tarifa);
        return {
            tarifa,
            costo: parseFloat((mins * tarifa).toFixed(3)),
            rango: tarifaPagada,
        };
    };

    /**
     * Cálculo de países para OnePBX:
     * Busca en tarifas_paises la tarifa que corresponde al pais del resumen
     * y multiplica minutos_pais × tarifa.
     * pais: string como "us", "mx", "ca" — se compara con tipo_llamada en mayúsculas.
     */
    const calcularCostoPaisOnePBX = (minutosPais, pais, tarifasPaises) => {
        if (!tarifasPaises || !tarifasPaises.length || !minutosPais || !pais) return null;
        const mins = Math.ceil(Number(minutosPais) || 0);
        if (mins === 0) return null;

        const paisKey = String(pais).toUpperCase().trim();
        const tarifaRow = tarifasPaises.find(
            (t) => String(t.tipo_llamada).toUpperCase().trim() === paisKey
        );
        if (!tarifaRow) return null;

        const tarifa = Number(tarifaRow.tarifa);
        if (tarifa === 0) return null;

        return {
            tarifa,
            costo: parseFloat((mins * tarifa).toFixed(3)),
            pais: paisKey,
        };
    };

    const calcularCostoExcedente = (minutosTotal, tarifasFlat) => {
        if (!tarifasFlat || !tarifasFlat.length) return null;
        const mins = Math.ceil(Number(minutosTotal) || 0);

        // Deduplicar por rango — quedarse con la tarifa más reciente (ORDER BY fecha_inicio DESC en backend)
        const rangosUnicos = {};
        for (const t of tarifasFlat) {
            const key = `${t.rango_min_inicio}-${t.rango_min_fin}`;
            if (!rangosUnicos[key]) rangosUnicos[key] = t;
        }
        // Ordenar de menor a mayor rango_min_inicio
        const rangos = Object.values(rangosUnicos).sort(
            (a, b) => Number(a.rango_min_inicio) - Number(b.rango_min_inicio)
        );

        // Buscar el rango que contiene los minutos
        let rango = rangos.find(
            (r) => mins >= Number(r.rango_min_inicio) && mins <= Number(r.rango_min_fin)
        );

        // Si no encuentra rango (minutos exceden el último rango definido), usar el último
        if (!rango && mins > 0) {
            rango = rangos[rangos.length - 1];
        }

        if (!rango) return null;

        const tarifa = Number(rango.tarifa);
        return {
            tarifa,
            costo: tarifa === 0 ? 0 : parseFloat((mins * tarifa).toFixed(3)),
            rango,
        };
    };

    // Fetch resumen
    // Consulta ligera del MES COMPLETO de fechaInicio, solo para el promedio/dia.
    // Reusa el mismo endpoint diario pero con el rango del mes; la tabla no la usa.
    const fetchPromedioMes = async () => {
        try {
            const d = ymdToLocalDate(fechaInicio) || new Date();
            const params = {
                empresa: empresasSeleccionadas.length ? empresasSeleccionadas.join(",") : undefined,
                cliente: clientesSeleccionados.length ? clientesSeleccionados.join(",") : undefined,
                fuente: carrier,
                fechaInicio: monthStartYmd(d),
                fechaFin: monthEndYmd(d),
            };
            const { data } = await axios.get(API_ROUTES.resumen.cliente.diario, { params });
            setResumenMes(data || []);
        } catch (error) {
            console.error("Error al traer promedio del mes:", error);
            setResumenMes([]);
        }
    };

    const fetchResumen = async () => {
        setLoading(true);
        fetchPromedioMes(); // en paralelo, no bloquea la tabla
        try {
            const params = {
                // Listas separadas por coma; el backend debe filtrar con IN (...)
                empresa: empresasSeleccionadas.length ? empresasSeleccionadas.join(",") : undefined,
                cliente: clientesSeleccionados.length ? clientesSeleccionados.join(",") : undefined,
                troncal: undefined,
                fuente: carrier, // rcc1 (Clearcom) | rn1 (Netbox)
                fechaInicio,
                fechaFin,
            };

            const endpoint = API_ROUTES.resumen.cliente.diario;

            const { data } = await axios.get(endpoint, { params });
            setResumenLocal(data || []);
        } catch (error) {
            console.error("Error al traer resumen:", error);
            toast.error("No se pudo obtener el resumen.");
        } finally {
            setLoading(false);
        }
    };

    // 🔥 CARGAR DATOS INICIALMENTE
    useEffect(() => {
        if (empresasSeleccionadas.length || !isAdmin) {
            fetchResumen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresasKey, carrier, modo, fechaInicio, fechaFin]);

    // 🔥 Resetear fechas cuando cambie el modo (solo después del primer render)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (modo === "mensual") {
            setFechaInicio(startOfMonth);
            setFechaFin(endOfMonth);
        } else {
            setFechaInicio(yesterdayStr);
            setFechaFin(todayStr);
        }
    }, [modo]);

    // Resumen total
    const resumenTotal = useMemo(() => {
        if (!resumen || !resumen.length) return null;

        const data = resumen;
        const total = data.reduce(
            (acc, curr) => {
                const costo_fijo = Number(curr.costo_fijo || 0);
                const costo_movil = Number(curr.costo_movil || 0);
                const costo_pais = Number(curr.costo_pais || 0);
                const costo_buzon = Number(curr.costo_buzon || 0);
                const costo_total = costo_fijo + costo_movil + costo_pais + costo_buzon;

                return {
                    llamadas_fijo: acc.llamadas_fijo + Number(curr.llamadas_fijo || 0),
                    minutos_fijo: acc.minutos_fijo + Number(curr.minutos_fijo || 0),
                    llamadas_movil: acc.llamadas_movil + Number(curr.llamadas_movil || 0),
                    minutos_movil: acc.minutos_movil + Number(curr.minutos_movil || 0),
                    buzon: acc.buzon + Number(curr.buzon || 0),
                    llamadas_pais: acc.llamadas_pais + Number(curr.llamadas_pais || 0),
                    minutos_pais: acc.minutos_pais + Number(curr.minutos_pais || 0),
                    llamadas_entrada:
                        acc.llamadas_entrada + Number(curr.llamadas_entrada || 0),
                    minutos_entrada:
                        acc.minutos_entrada + Number(curr.minutos_entrada || 0),
                    costo_fijo: acc.costo_fijo + costo_fijo,
                    costo_movil: acc.costo_movil + costo_movil,
                    costo_pais: acc.costo_pais + costo_pais,
                    costo_buzon: acc.costo_buzon + costo_buzon,
                    costo_total: acc.costo_total + costo_total,
                };
            },
            {
                llamadas_fijo: 0,
                minutos_fijo: 0,
                llamadas_movil: 0,
                minutos_movil: 0,
                buzon: 0,
                llamadas_pais: 0,
                minutos_pais: 0,
                llamadas_entrada: 0,
                minutos_entrada: 0,
                costo_fijo: 0,
                costo_movil: 0,
                costo_pais: 0,
                costo_buzon: 0,
                costo_total: 0,
            }
        );

        total.costo_fijo = Number(total.costo_fijo.toFixed(3));
        total.costo_movil = Number(total.costo_movil.toFixed(3));
        total.costo_pais = Number(total.costo_pais.toFixed(3));
        total.costo_buzon = Number(total.costo_buzon.toFixed(3));
        total.costo_total = Number(total.costo_total.toFixed(3));

        return total;
    }, [resumen]);

    // Promedio de costo por dia, ESTATICO del mes:
    // se calcula sobre el mes completo de fechaInicio (resumenMes), no sobre el
    // rango de la tabla. Asi, aunque selecciones un solo dia, muestra el promedio
    // del mes = costo total del mes / pares (cliente, dia) distintos del mes.
    const promedios = useMemo(() => {
        const base = resumenMes && resumenMes.length ? resumenMes : null;
        if (!base) return null;

        const dias = new Set();
        const clienteDias = new Set();
        let costoTotalMes = 0;
        // Agregado por cliente para el promedio diario INDIVIDUAL (cada cliente su costo/dias)
        const porCliente = new Map();
        for (const r of base) {
            const dia = String(r.fecha || "").slice(0, 10);
            if (!dia) continue;
            dias.add(dia);
            const cli = String(r.cliente_id ?? r.cliente ?? "").toLowerCase().trim();
            clienteDias.add(`${cli}|${dia}`);
            const costoRow =
                Number(r.costo_fijo || 0) +
                Number(r.costo_movil || 0) +
                Number(r.costo_pais || 0) +
                Number(r.costo_buzon || 0);
            costoTotalMes += costoRow;

            if (!porCliente.has(cli)) porCliente.set(cli, { costo: 0, dias: new Set() });
            const acc = porCliente.get(cli);
            acc.costo += costoRow;
            acc.dias.add(dia);
        }

        const nDias = dias.size;
        const nClienteDias = clienteDias.size;
        const safeDiv = (num, den) => (den > 0 ? Number(num) / Number(den) : 0);

        // Promedio diario individual: media de (costo_cliente / dias_cliente) sobre los clientes.
        // Con 1 cliente = su promedio diario exacto.
        let sumProm = 0;
        let nClientes = 0;
        for (const { costo, dias: diasCli } of porCliente.values()) {
            if (diasCli.size > 0) {
                sumProm += costo / diasCli.size;
                nClientes += 1;
            }
        }
        const costoPromDiarioIndividual = nClientes > 0 ? sumProm / nClientes : 0;

        return {
            nDias,
            nClientes,
            nClienteDias,
            costoTotalMes,
            costoPromClienteDia: safeDiv(costoTotalMes, nClienteDias),
            costoPromDia: safeDiv(costoTotalMes, nDias),
            costoPromDiarioIndividual,
        };
    }, [resumenMes]);

    useEffect(() => {
        if (setResumen && resumenTotal) {
            setResumen(resumenTotal);
        }
        if (setDetalleData) {
            setDetalleData(resumen); // ← Pasar el array completo con todos los días
        }
    }, [resumenTotal, resumen, setResumen, setDetalleData]);

    // Cálculo agregado OnePBX: suma del costo estimado de todas las filas del período
    const onepbxResumen = useMemo(() => {
        if (!onepbxData.length || !resumen.length) return null;

        // Agrupar por cliente_id Y nombre (fallback) sumando minutos del período completo
        const porClienteId = {};
        const porClienteNombre = {};
        for (const fila of resumen) {
            const addTo = (bucket) => {
                bucket.minFijo += Number(fila.minutos_fijo || 0);
                bucket.minMovil += Number(fila.minutos_movil || 0);
                // Acumular minutos por país (ej. { US: 1600, MX: 200 })
                const pais = String(fila.pais || "").toUpperCase().trim();
                if (pais) {
                    bucket.minPaises[pais] = (bucket.minPaises[pais] || 0) + Number(fila.minutos_pais || 0);
                }
            };

            if (fila.cliente_id) {
                const cid = String(fila.cliente_id);
                if (!porClienteId[cid]) porClienteId[cid] = { minFijo: 0, minMovil: 0, minPaises: {} };
                addTo(porClienteId[cid]);
            }
            const nombre = String(fila.cliente || "").toLowerCase().trim();
            if (nombre) {
                if (!porClienteNombre[nombre]) porClienteNombre[nombre] = { minFijo: 0, minMovil: 0, minPaises: {} };
                addTo(porClienteNombre[nombre]);
            }
        }

        let costoTotalEstimado = 0;
        const detalle = [];

        for (const o of onepbxData) {
            const cidKey = String(o.cliente_id);
            const nombreKey = String(o.cliente_nombre || "").toLowerCase().trim();
            const mins = porClienteId[cidKey] || porClienteNombre[nombreKey];
            if (!mins) continue;

            const minutosTotal = mins.minFijo + mins.minMovil;

            // Costo Flat (fijo + móvil)
            const resultadoFlat = calcularCostoOnePBX(minutosTotal, o.tarifas_flat);
            let costoCliente = resultadoFlat?.costo ?? 0;

            // Costo países (US, MX, etc.) — suma cada país con su tarifa
            const costosPaises = [];
            for (const [pais, minPais] of Object.entries(mins.minPaises)) {
                const rp = calcularCostoPaisOnePBX(minPais, pais, o.tarifas_paises || []);
                if (rp) {
                    costoCliente += rp.costo;
                    costosPaises.push(rp);
                }
            }

            costoTotalEstimado += costoCliente;
            detalle.push({ ...o, minutosTotal, resultadoFlat, costosPaises, costoCliente: parseFloat(costoCliente.toFixed(3)) });
        }

        return { costoTotalEstimado: parseFloat(costoTotalEstimado.toFixed(3)), detalle };
    }, [onepbxData, resumen]);

    const resumenFiltrado = useMemo(() => {
        if (!resumen || !resumen.length) return [];
        if (!quickSearch) return resumen;
        const busqueda = quickSearch.toLowerCase();
        return resumen.filter((fila) =>
            String(fila.troncal || "").toLowerCase().includes(busqueda) ||
            String(fila.cliente || "").toLowerCase().includes(busqueda) ||
            String(fila.pais || "").toLowerCase().includes(busqueda) ||
            String(fila.empresa || "").toLowerCase().includes(busqueda)
        );
    }, [resumen, quickSearch]);

    const resumenOrdenado = useMemo(() => {
        if (!resumenFiltrado || !resumenFiltrado.length) return [];
        const sorted = [...resumenFiltrado].sort((a, b) => {
            const va = getSortableValue(a, columnaOrdenada);
            const vb = getSortableValue(b, columnaOrdenada);
            if (va < vb) return orden === "asc" ? -1 : 1;
            if (va > vb) return orden === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [resumenFiltrado, columnaOrdenada, orden]);

    // Exporta el resumen del rango a CSV en el formato de Telco 1:
    // una fila por cliente, conteos con separador de miles y costos como $x.xxx.
    // Respeta los filtros actuales (empresa / cliente / pais / rango) y la busqueda
    // rapida visible en la tabla. Con un cliente seleccionado sale una sola fila;
    // con "Todos", una fila por cliente.
    const descargarCSV = () => {
        const filas =
            resumenOrdenado && resumenOrdenado.length ? resumenOrdenado : resumen;

        if (!filas || !filas.length) {
            toast.info("No hay datos para exportar con los filtros actuales.");
            return;
        }

        const csvCell = (v) => {
            const s = v == null ? "" : String(v);
            return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };

        // Formato de conteos (entero con separador de miles) y de costos
        // (numero plano, sin simbolo ni separador de miles, 3 decimales).
        const numFmt = (v) => Number(v || 0).toLocaleString("en-US");
        const costoFmt = (v) => Number(v || 0).toFixed(3);

        // Si las filas traen "carrier" (modo Todo), separamos por empresa+carrier
        // y agregamos una columna Carrier; si no, una fila por empresa como siempre.
        const hasCarrier = filas.some((r) => r.carrier);

        const grupos = new Map();
        for (const r of filas) {
            const etiqueta = r.empresa || r.cliente || "";
            const carrier = r.carrier || "";
            const key = hasCarrier
                ? `emp:${String(etiqueta).toLowerCase().trim()}|car:${String(carrier).toLowerCase().trim()}`
                : `emp:${String(etiqueta).toLowerCase().trim()}`;

            if (!grupos.has(key)) {
                grupos.set(key, {
                    cliente: etiqueta,
                    carrier,
                    llamadas_fijo: 0,
                    minutos_fijo: 0,
                    llamadas_movil: 0,
                    minutos_movil: 0,
                    llamadas_pais: 0,
                    minutos_pais: 0,
                    buzon: 0,
                    costo_fijo: 0,
                    costo_movil: 0,
                    costo_pais: 0,
                    costo_buzon: 0,
                });
            }

            const acc = grupos.get(key);
            acc.llamadas_fijo += Number(r.llamadas_fijo || 0);
            acc.minutos_fijo += Number(r.minutos_fijo || 0);
            acc.llamadas_movil += Number(r.llamadas_movil || 0);
            acc.minutos_movil += Number(r.minutos_movil || 0);
            acc.llamadas_pais += Number(r.llamadas_pais || 0);
            acc.minutos_pais += Number(r.minutos_pais || 0);
            acc.buzon += Number(r.buzon || 0);
            acc.costo_fijo += Number(r.costo_fijo || 0);
            acc.costo_movil += Number(r.costo_movil || 0);
            acc.costo_pais += Number(r.costo_pais || 0);
            acc.costo_buzon += Number(r.costo_buzon || 0);
        }

        const registros = [...grupos.values()].sort((a, b) => {
            const porEmp = String(a.cliente).localeCompare(String(b.cliente), "es");
            if (porEmp !== 0) return porEmp;
            return String(a.carrier).localeCompare(String(b.carrier), "es");
        });

        const encabezados = [
            "Cliente",
            ...(hasCarrier ? ["Carrier"] : []),
            "llamadas_fijos",
            "minutos_fijos",
            "llamadas_moviles",
            "minutos_moviles",
            "llamadas_pais",
            "minutos_pais",
            "buzones",
            "costo_fijo",
            "costo_movil",
            "costo_pais",
            "costo_buzon",
            "costo_total",
        ];

        const lineas = registros.map((c) => {
            const costoTotal =
                c.costo_fijo + c.costo_movil + c.costo_pais + c.costo_buzon;

            return [
                c.cliente,
                ...(hasCarrier ? [c.carrier] : []),
                numFmt(c.llamadas_fijo),
                numFmt(c.minutos_fijo),
                numFmt(c.llamadas_movil),
                numFmt(c.minutos_movil),
                numFmt(c.llamadas_pais),
                numFmt(c.minutos_pais),
                numFmt(c.buzon),
                costoFmt(c.costo_fijo),
                costoFmt(c.costo_movil),
                costoFmt(c.costo_pais),
                costoFmt(c.costo_buzon),
                costoFmt(costoTotal),
            ];
        });

        const matriz = [encabezados, ...lineas];
        // BOM para que Excel respete acentos; CRLF entre filas
        const csv =
            "\uFEFF" +
            matriz.map((fila) => fila.map(csvCell).join(",")).join("\r\n");

        // Nombre de archivo: resumen_[cliente]_<inicio>_a_<fin>.csv
        const slug = (s) =>
            String(s || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-zA-Z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "")
                .toLowerCase();

        const clienteUnico = registros.length === 1 ? registros[0].cliente : null;
        const partes = ["resumen"];
        if (clienteUnico) partes.push(slug(clienteUnico));
        partes.push(fechaInicio, "a", fechaFin);
        const nombreArchivo = `${partes.join("_")}.csv`;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nombreArchivo;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(
            `CSV generado (${registros.length} ${registros.length === 1 ? "cliente" : "clientes"
            }).`
        );
    };

    // Exporta el DETALLE crudo del rango (una fila por registro, sin agrupar).
    // Sirve para ver los dias de un cliente o de todos tal como salen: no
    // consolida ni suma por fecha. Respeta los filtros actuales (empresa /
    // cliente / pais / rango) y la busqueda rapida visible en la tabla.
    const descargarCSVDetalle = () => {
        const filas =
            resumenOrdenado && resumenOrdenado.length ? resumenOrdenado : resumen;

        if (!filas || !filas.length) {
            toast.info("No hay datos para exportar con los filtros actuales.");
            return;
        }

        const csvCell = (v) => {
            const s = v == null ? "" : String(v);
            return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };

        const numFmt = (v) => Number(v || 0).toLocaleString("en-US");
        const costoFmt = (v) => Number(v || 0).toFixed(3);

        // Pre-agregado por cliente: costo total del rango y numero de dias distintos,
        // para el promedio de costo por dia (mismo valor en las filas de cada cliente).
        const aggCliente = new Map();
        for (const r of filas) {
            const key = String(r.cliente_id ?? r.cliente ?? "");
            const dia = String(r.fecha || "").slice(0, 10);
            const costoTot =
                Number(r.costo_fijo || 0) +
                Number(r.costo_movil || 0) +
                Number(r.costo_pais || 0) +
                Number(r.costo_buzon || 0);
            if (!aggCliente.has(key)) aggCliente.set(key, { costo: 0, dias: new Set() });
            const acc = aggCliente.get(key);
            acc.costo += costoTot;
            if (dia) acc.dias.add(dia);
        }
        const promedioCostoDia = (r) => {
            const key = String(r.cliente_id ?? r.cliente ?? "");
            const agg = aggCliente.get(key);
            if (!agg || agg.dias.size === 0) return "0.000";
            return (agg.costo / agg.dias.size).toFixed(3);
        };

        const encabezados = [
            "Empresa",
            "Cliente",
            "Pais",
            "Fecha",
            "llamadas_fijos",
            "minutos_fijos",
            "llamadas_moviles",
            "minutos_moviles",
            "buzones",
            "llamadas_pais",
            "minutos_pais",
            "llamadas_entrada",
            "minutos_entrada",
            "costo_fijo",
            "costo_movil",
            "costo_pais",
            "costo_buzon",
            "costo_total",
            "promedio_costo_dia",
        ];

        // Una fila por registro tal cual, en el mismo orden que muestra la tabla.
        const lineas = filas.map((r) => {
            const costoTotal =
                Number(r.costo_fijo || 0) +
                Number(r.costo_movil || 0) +
                Number(r.costo_pais || 0) +
                Number(r.costo_buzon || 0);

            return [
                r.empresa || "",
                r.cliente || "",
                String(r.pais || "").toUpperCase(),
                formatFecha(r.fecha, modo),
                numFmt(r.llamadas_fijo),
                numFmt(r.minutos_fijo),
                numFmt(r.llamadas_movil),
                numFmt(r.minutos_movil),
                numFmt(r.buzon),
                numFmt(r.llamadas_pais),
                numFmt(r.minutos_pais),
                numFmt(r.llamadas_entrada),
                numFmt(r.minutos_entrada),
                costoFmt(r.costo_fijo),
                costoFmt(r.costo_movil),
                costoFmt(r.costo_pais),
                costoFmt(r.costo_buzon),
                costoFmt(costoTotal),
                promedioCostoDia(r),
            ];
        });

        const matriz = [encabezados, ...lineas];
        const csv =
            "\uFEFF" +
            matriz.map((fila) => fila.map(csvCell).join(",")).join("\r\n");

        const slug = (s) =>
            String(s || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-zA-Z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "")
                .toLowerCase();

        // Nombre: detalle_[cliente|empresa]_<inicio>_a_<fin>.csv
        const clientesUnicos = new Set(
            filas.map((r) => String(r.cliente || "").toLowerCase().trim())
        );
        const empresasUnicas = new Set(
            filas.map((r) => String(r.empresa || "").toLowerCase().trim())
        );

        const partes = ["detalle"];
        if (clientesUnicos.size === 1 && filas[0].cliente) {
            partes.push(slug(filas[0].cliente));
        } else if (empresasUnicas.size === 1 && filas[0].empresa) {
            partes.push(slug(filas[0].empresa));
        }
        partes.push(fechaInicio, "a", fechaFin);
        const nombreArchivo = `${partes.join("_")}.csv`;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nombreArchivo;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(
            `Detalle generado (${filas.length} ${filas.length === 1 ? "registro" : "registros"
            }).`
        );
    };

    return (
        <div className="p-3 sm:p-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <h1 className="text-xl font-bold text-slate-800">Panel de Reportes</h1>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-md shadow-sm border bg-white">
                        <button
                            type="button"
                            onClick={() => cambiarCarrier("rcc1")}
                            title="Clearcom (telco_resumen)"
                            className={`px-3 py-1 text-xs font-medium rounded-l-md ${carrier === "rcc1"
                                ? "bg-indigo-600 text-white"
                                : "bg-white text-slate-700 hover:bg-indigo-50"
                                }`}
                        >
                            RCC1
                        </button>
                        <button
                            type="button"
                            onClick={() => cambiarCarrier("rn1")}
                            title="Netbox (telco_resumen_netbox)"
                            className={`px-3 py-1 text-xs font-medium border-l ${carrier === "rn1"
                                ? "bg-indigo-600 text-white"
                                : "bg-white text-slate-700 hover:bg-indigo-50"
                                }`}
                        >
                            RN1
                        </button>
                        <button
                            type="button"
                            onClick={() => cambiarCarrier("todo")}
                            title="Unión Clearcom + Netbox (carrier_cliente_map)"
                            className={`px-3 py-1 text-xs font-medium rounded-r-md border-l ${carrier === "todo"
                                ? "bg-indigo-600 text-white"
                                : "bg-white text-slate-700 hover:bg-indigo-50"
                                }`}
                        >
                            Todo
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={fetchResumen}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-600 text-white text-xs font-medium px-3 py-1 hover:bg-blue-700"
                        disabled={loading}
                    >
                        {loading ? "Actualizando..." : "Actualizar"}
                    </button>

                    <button
                        type="button"
                        onClick={descargarCSV}
                        disabled={loading || !resumen.length}
                        title="Descargar el resumen del rango (una fila por empresa, sumado)"
                        className="inline-flex items-center gap-1 rounded-md bg-slate-700 text-white text-xs font-medium px-3 py-1 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                        </svg>
                        Descargar resumen
                    </button>

                    <button
                        type="button"
                        onClick={descargarCSVDetalle}
                        disabled={loading || !resumen.length}
                        title="Descargar el detalle crudo del rango (una fila por dia, sin agrupar)"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-700 text-white text-xs font-medium px-3 py-1 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        Descargar detalle
                    </button>
                </div>
            </div>

            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <MultiSelect
                    label="Empresa"
                    values={empresasSeleccionadas}
                    onChange={(vals) => { setEmpresasSeleccionadas(vals); setClientesSeleccionados([]); }}
                    options={empresas}
                    placeholder="Todas"
                    disabled={!isAdmin}
                    valueKey="id"
                    labelKey="nombre"
                />

                <MultiSelect
                    label="Cliente"
                    values={clientesSeleccionados}
                    onChange={setClientesSeleccionados}
                    options={clientes}
                    placeholder="Todos"
                    valueKey="cliente_id"
                    labelKey="cliente"
                />

                {modo === "mensual" ? (
                    <>
                        <div className="space-y-2">
                            <label className="block text-xs font-semibold text-slate-600">
                                Mes Inicio
                            </label>

                            <Flatpickr
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                                value={ymdToMonthLocalDate(fechaInicio)}
                                options={monthPickerOptions}
                                onChange={(selectedDates) => {
                                    const d = selectedDates?.[0];
                                    if (!d) return;

                                    const nextInicio = monthStartYmd(d);
                                    setFechaInicio(nextInicio);

                                    // si el fin quedó antes del inicio, empuja al mismo mes
                                    const finMonth = ymdToMonthLocalDate(fechaFin);
                                    const iniMonth = ymdToMonthLocalDate(nextInicio);
                                    if (finMonth && iniMonth && finMonth < iniMonth) {
                                        setFechaFin(monthEndYmd(d));
                                    }
                                }}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-xs font-semibold text-slate-600">
                                Mes Fin
                            </label>

                            <Flatpickr
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                                value={ymdToMonthLocalDate(fechaFin)}
                                options={{
                                    ...monthPickerOptions,
                                }}
                                onChange={(selectedDates) => {
                                    const d = selectedDates?.[0];
                                    if (!d) return;

                                    const nextInicio = monthStartYmd(d);
                                    const nextFin = monthEndYmd(d);
                                    const iniMonth = ymdToMonthLocalDate(fechaInicio);
                                    const finMonth = ymdToMonthLocalDate(nextInicio);
                                    if (iniMonth && finMonth && finMonth < iniMonth) {
                                        setFechaInicio(nextInicio);
                                    }

                                    setFechaFin(nextFin);
                                }}
                            />

                        </div>
                    </>
                ) : (
                    <>
                        <div className="space-y-2">
                            <label className="block text-xs font-semibold text-slate-600">
                                Fecha Inicio
                            </label>

                            <Flatpickr
                                key={`inicio-diario-${fechaInicio}`}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                                value={ymdToLocalDate(fechaInicio)}
                                options={dayPickerOptions}
                                onChange={(selectedDates) => {
                                    const d = selectedDates?.[0];
                                    if (!d) return;

                                    const next = toYmd(d);     //  siempre YYYY-MM-DD estable
                                    setFechaInicio(next);
                                    setFechaFin(next);         //  por defecto, mismo día
                                }}
                            />

                        </div>

                        <div className="space-y-2">
                            <label className="block text-xs font-semibold text-slate-600">
                                Fecha Fin
                            </label>

                            <Flatpickr
                                key={`fin-diario-${fechaFin}`}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                                value={ymdToLocalDate(fechaFin)}
                                options={{
                                    ...dayPickerOptions,
                                    minDate: ymdToLocalDate(fechaInicio),
                                }}
                                onChange={(selectedDates) => {
                                    const d = selectedDates?.[0];
                                    if (!d) return;
                                    setFechaFin(toYmd(d));
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            {resumenTotal && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border min-w-0 border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold mb-1">Costo Fijo</p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-blue-700">
                            $
                            {resumenTotal.costo_fijo.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border min-w-0 border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold mb-1">
                            Costo Móvil
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-blue-700">
                            $
                            {resumenTotal.costo_movil.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border min-w-0 border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold mb-1">Costo País</p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-blue-700">
                            $
                            {resumenTotal.costo_pais.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border min-w-0 border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold mb-1">
                            Costo Buzón
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-blue-700">
                            $
                            {resumenTotal.costo_buzon.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border min-w-0 border-green-200">
                        <p className="text-xs text-green-600 font-semibold mb-1">
                            Costo Total
                        </p>
                        {/* <p className="text-[10px] text-gray-500 italic">(sin País)</p> */}
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-green-700">
                            $
                            {resumenTotal.costo_total.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                        {onepbxResumen && (
                            <div className="mt-1.5 pt-1.5 border-t border-green-200">
                                <p className="text-[10px] text-green-600 font-medium leading-tight">
                                    + Est. OnePBX
                                </p>
                                <p className={`text-base font-bold leading-tight ${onepbxResumen.costoTotalEstimado > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                                    ${onepbxResumen.costoTotalEstimado.toLocaleString("en-US", {
                                        minimumFractionDigits: 3,
                                        maximumFractionDigits: 3,
                                    })}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {promedios && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border min-w-0 border-amber-200">
                        <p className="text-xs text-amber-600 font-semibold mb-1">
                            Costo prom. cliente/día (mes)
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-amber-700">
                            $
                            {promedios.costoPromClienteDia.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                        <p className="text-[10px] text-amber-600/80 mt-0.5">
                            {promedios.nClienteDias.toLocaleString("es-MX")}{" "}
                            {promedios.nClienteDias === 1 ? "cliente-día" : "clientes-día"} · mes completo
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border min-w-0 border-orange-200">
                        <p className="text-xs text-orange-600 font-semibold mb-1">
                            Costo prom. consumo diario / cliente
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums text-orange-700">
                            $
                            {promedios.costoPromDiarioIndividual.toLocaleString("en-US", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                            })}
                        </p>
                        <p className="text-[10px] text-orange-600/80 mt-0.5">
                            {promedios.nClientes.toLocaleString("es-MX")}{" "}
                            {promedios.nClientes === 1 ? "cliente" : "clientes"} · costo/día por cliente
                        </p>
                    </div>
                </div>
            )}

            <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
                <div className="border-b px-3 py-2 flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-sm font-semibold text-slate-700">
                                Detalle de Consumo
                            </h2>
                            {/* Badge OnePBX activo */}
                            {onepbxResumen?.detalle?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {onepbxResumen.detalle.map((o, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold px-2 py-0.5 border border-violet-200"
                                            title={`Cliente: ${o.cliente_nombre}`}
                                        >
                                            <svg className="w-2.5 h-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                            {o.producto_nombre}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <p className="text-[11px] text-slate-500">
                            {modo === "mensual" ? "Resumen General" : "Resumen por días"}
                        </p>
                    </div>
                    <div className="text-[11px] text-slate-500">
                        Registros: <strong>{resumenOrdenado.length}</strong>
                    </div>
                </div>

                {/*  SCROLL HORIZONTAL Y VERTICAL CON ALTURA MÁXIMA */}
                <div
                    className="overflow-auto"
                    style={{
                        maxHeight: "600px",
                        overflowX: "auto",
                        overflowY: "auto",
                    }}
                >
                    <table className="text-xs w-full">
                        <thead className="bg-slate-50 border-b sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 text-left whitespace-nowrap bg-slate-50">
                                    Empresa
                                </th>
                                <th
                                    onClick={() => ordenarPor("cliente")}
                                    className="px-3 py-2 text-left cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Cliente{" "}
                                    {columnaOrdenada === "cliente"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("pais")}
                                    className="px-3 py-2 text-left cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    País{" "}
                                    {columnaOrdenada === "pais"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                {modo !== "diario" && (
                                    <th
                                        onClick={() => ordenarPor("troncal")}
                                        className="px-3 py-2 text-left cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                    >
                                        Troncal{" "}
                                        {columnaOrdenada === "troncal"
                                            ? orden === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                )}
                                <th
                                    onClick={() => ordenarPor("fecha")}
                                    className="px-3 py-2 text-left cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Fecha{" "}
                                    {columnaOrdenada === "fecha"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("llamadas_fijo")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Llam. Fijo{" "}
                                    {columnaOrdenada === "llamadas_fijo"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("minutos_fijo")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Min. Fijo{" "}
                                    {columnaOrdenada === "minutos_fijo"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("llamadas_movil")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Llam. Móvil{" "}
                                    {columnaOrdenada === "llamadas_movil"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("minutos_movil")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Min. Móvil{" "}
                                    {columnaOrdenada === "minutos_movil"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("buzon")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Buzón{" "}
                                    {columnaOrdenada === "buzon"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("llamadas_pais")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Llam. País{" "}
                                    {columnaOrdenada === "llamadas_pais"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("minutos_pais")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Min. País{" "}
                                    {columnaOrdenada === "minutos_pais"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("llamadas_entrada")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Llam. Entr.{" "}
                                    {columnaOrdenada === "llamadas_entrada"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("minutos_entrada")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Min. Entr.{" "}
                                    {columnaOrdenada === "minutos_entrada"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("costo_fijo")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Costo Fijo{" "}
                                    {columnaOrdenada === "costo_fijo"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("costo_movil")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Costo Móvil{" "}
                                    {columnaOrdenada === "costo_movil"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("costo_pais")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Costo País{" "}
                                    {columnaOrdenada === "costo_pais"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("costo_buzon")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Costo Buzón{" "}
                                    {columnaOrdenada === "costo_buzon"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th
                                    onClick={() => ordenarPor("costo_total")}
                                    className="px-5 py-3 text-right cursor-pointer hover:underline whitespace-nowrap bg-slate-50"
                                >
                                    Costo Total{" "}
                                    {columnaOrdenada === "costo_total"
                                        ? orden === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {loading && (
                                <tr>
                                    <td
                                        colSpan={modo !== "diario" ? 18 : 17}
                                        className="px-4 py-4 text-center text-xs text-slate-400"
                                    >
                                        Cargando...
                                    </td>
                                </tr>
                            )}

                            {!loading && resumenOrdenado.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={modo !== "diario" ? 18 : 17}
                                        className="px-4 py-4 text-center text-xs text-slate-400"
                                    >
                                        Sin datos para los filtros seleccionados.
                                    </td>
                                </tr>
                            )}

                            {!loading &&
                                resumenOrdenado.map((fila, idx) => {
                                    const costoTotalFila =
                                        Number(fila.costo_fijo || 0) +
                                        Number(fila.costo_movil || 0) +
                                        Number(fila.costo_pais || 0) +
                                        Number(fila.costo_buzon || 0);

                                    return (
                                        <tr key={idx} className="hover:bg-slate-50">
                                            <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                                                {fila.empresa}
                                            </td>
                                            <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                                                {fila.cliente}
                                            </td>
                                            <td className="px-3 py-2 text-[11px] uppercase whitespace-nowrap">
                                                {fila.pais}
                                            </td>
                                            {modo !== "diario" && (
                                                <td
                                                    className="px-3 py-2 text-[11px] whitespace-nowrap"
                                                    title={fila.troncal}
                                                >
                                                    {fila.troncal}
                                                </td>
                                            )}
                                            <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                                                {formatFecha(fila.fecha, modo)}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.llamadas_fijo.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.minutos_fijo.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.llamadas_movil.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.minutos_movil.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.buzon.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.llamadas_pais.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {fila.minutos_pais.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {Number(fila.llamadas_entrada || 0).toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {Number(fila.minutos_entrada || 0).toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                $
                                                {Number(fila.costo_fijo || 0).toLocaleString("en-US", {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3,
                                                })}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                $
                                                {Number(fila.costo_movil || 0).toLocaleString("en-US", {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3,
                                                })}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                $
                                                {Number(fila.costo_pais || 0).toLocaleString("en-US", {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3,
                                                })}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                $
                                                {Number(fila.costo_buzon || 0).toLocaleString("en-US", {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3,
                                                })}
                                            </td>
                                            <td className="px-5 py-3 text-right font-bold whitespace-nowrap">
                                                $
                                                {costoTotalFila.toLocaleString("en-US", {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3,
                                                })}
                                            </td>
                                        </tr>
                                    );
                                })}

                            {resumenOrdenado.length > 0 && resumenTotal && (
                                <tr className="bg-slate-100 font-semibold sticky bottom-0">
                                    <td className="px-3 py-2 text-[11px] whitespace-nowrap bg-slate-100">
                                        TOTAL
                                    </td>
                                    <td className="px-3 py-2 text-[11px] bg-slate-100" />
                                    <td className="px-3 py-2 text-[11px] bg-slate-100" />
                                    {modo !== "diario" && (
                                        <td className="px-3 py-2 text-[11px] bg-slate-100" />
                                    )}
                                    <td className="px-3 py-2 text-[11px] bg-slate-100" />
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.llamadas_fijo.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.minutos_fijo.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.llamadas_movil.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.minutos_movil.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.buzon.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.llamadas_pais.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.minutos_pais.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.llamadas_entrada.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        {resumenTotal.minutos_entrada.toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        ${resumenTotal.costo_fijo.toLocaleString("en-US", {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                        })}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        ${resumenTotal.costo_movil.toLocaleString("en-US", {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                        })}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        ${resumenTotal.costo_pais.toLocaleString("en-US", {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                        })}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap bg-slate-100">
                                        ${resumenTotal.costo_buzon.toLocaleString("en-US", {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                        })}
                                    </td>
                                    <td className="px-5 py-3 text-right font-bold whitespace-nowrap bg-slate-100">
                                        ${resumenTotal.costo_total.toLocaleString("en-US", {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                        })}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ReportesPanel;